import { CheckIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import {
  BasicProductData,
  EnrichedAccessory,
  formatPerfValue
} from 'components/layout/modals/compatible-combinations-browser-modal';
import { clearDuctedPairPick, setDuctedPairPick } from 'features/ui';
import { useComparisonSelection } from 'hooks/use-comparison-selection';
import { DuctedComponentSwapHandler } from 'hooks/use-ducted-component-swap';
import usePendingCompatibilityAdd from 'hooks/use-pending-compatibility-add';
import useQuotableAdd from 'hooks/use-quotable-add';
import { useEffect, useMemo, useState } from 'react';
import { computeCombinationTotalPrice } from 'utils/combination-pricing';
import { KEPT_PAIRING_TAG } from 'utils/compatibility-systems';
import {
  AccessoryPick,
  AccessoryPicks,
  groupCombinationAccessories,
  picksMatchingProductIds,
  quotableAccessoryProductIds,
  resolveAccessoriesString
} from 'utils/ducted-accessory-groups';
import {
  isPickedForPair,
  selectDuctedBundleMode,
  selectDuctedPairName
} from 'utils/ducted-bundle-picks';
import { withDuctedCompareIdentity } from 'utils/ducted-compare-identity';
import { comparedIdForSelection } from 'utils/ducted-compare-state';
import { selectDuctedPairExistingSystemLabel } from 'utils/ducted-design';
import { selectDuctedPairLoads } from 'utils/ducted-pair-context';
import { syncDuctedPairPick } from 'utils/ducted-pair-pick-sync';
import {
  buildDuctedDetailDisplay,
  existingSizeForDisplay
} from 'utils/ducted-detail-display';
import {
  DUCTED_PERFORMANCE_UNAVAILABLE,
  ductedPerformanceFromCombination
} from 'utils/ducted-performance-display';
import {
  formatTonnageLabel,
  keptSourcePhrase,
  parseDisplayAttributes,
  RatingRowKey,
  ratingsApplicability
} from 'utils/ducted-ratings';
import {
  estimateSublabel,
  readDuctedOperatingCost,
  supplementalHeatingCostLine
} from 'utils/ducted-system-metrics';
import {
  ductedDuplicateNameIndexes,
  numberedDuctedSystemLabel
} from 'utils/duplicate-system-names';
import { formatPrice } from 'utils/helpers';
import { systemTypeCodeFuel } from 'utils/kept-system-type-annotation';
import {
  buildSelectedCombination,
  canSelectCombination
} from 'utils/quotable-add-pipeline';
import { resolveTonnageSeed } from 'utils/tonnage-logic';
import { combinationConfigLines } from './combination-config-lines';
import {
  compareActionText,
  PANEL_COMPARE_LABELS,
  pairSelectLabels
} from './compare-action-text';
import DuctedComponentsTable from './ducted-components-table';
import DuctedZonePerformanceSection from './ducted-zone-performance-section';
import {
  CostRow,
  CoverageGrid,
  OperatingCostAmount,
  OverviewCard,
  PerformanceInYourAreaTitle
} from './overview-cards';

// What the ducted Systems rail lifts to the view on card selection
// (AHA-6681 CS-3). `modelSpecs` rides along when the card's models have
// spec rows, so the header lines can prefix each component's size.
export type DuctedPanelSelection = {
  cardId: string;
  systemUniqueId: string;
  title: string;
  typeLabel?: string;
  // Set when the card came from the kept-constrained result set (AHA-6681
  // sub-build 4): the panel renders the "Pairs with Customer's Equipment"
  // chip and the add pipeline carries the marker into the manufactured
  // system's tags (comparison + quote artifacts).
  keptPairing?: boolean;
  // Kept-aware "Customer's <equipment>" display label (AHA-6681 display
  // titles); set only for kept selections. Feeds the manufactured system's
  // default `name` — the raw SystemTypeLabel stays untouched for grouping
  // and the Phase 5/6 payloads.
  displayTypeLabel?: string;
  // The system-type compatibility code behind the card (AHA-6796 LP
  // ratings display): exact for representative cards (the rail fetches
  // reps per typeCode); tail cards resolve it by facet label and leave it
  // undefined when unresolvable (the panel then falls back to the spec
  // fuel display). Lets the ratings panel show "Propane (LP)" for an
  // lp-token system — the spec tables physically carry only Natural Gas
  // rows for LP combos (NG furnace models + lp_conversion_kit).
  typeCode?: string;
  combination: Record<string, unknown>;
  productData: Record<string, BasicProductData>;
  accessoryData: Record<string, EnrichedAccessory>;
  modelSpecs?: Record<string, Record<string, unknown>>;
};

type DuctedSystemDetailPanelProps = Readonly<{
  selection: DuctedPanelSelection | null;
  comparedSystemId?: string;
  onComparedIdMapped?: (
    cardId: string,
    systemId: string,
    selection: DuctedPanelSelection
  ) => void;
  onComponentSwap?: DuctedComponentSwapHandler;
}>;

const RATING_LABELS: Record<RatingRowKey, string> = {
  SEER2: 'SEER2',
  EER2: 'EER2',
  HSPF2: 'HSPF2',
  COP5: 'COP5',
  AFUE: 'AFUE',
  CoolingCapacity: 'Cooling Capacity',
  HeatingCapacity: 'Heating Capacity',
  tonnage: 'Tonnage',
  refrigerant: 'Refrigerant',
  stages: 'Stages',
  fuelType: 'Fuel Type'
};

// Derived-value treatment (AHA-6681 honesty rule): a visible marker
// distinguishing derived values from AHRI-rated combo data, e.g.
// "~60,000 · from your furnace" (kept) / "~36,000 · estimated" (estimate).
function DerivedValue({
  formatted,
  suffix
}: Readonly<{ formatted: string; suffix: string }>) {
  return (
    <>
      ~{formatted}
      <span className='text-[11px] text-gray-400'> · {suffix}</span>
    </>
  );
}

// Ducted counterpart of EquipmentComparisonMiddlePanel (AHA-6681 CS-3):
// renders the compatible-combination data (header, ratings, equipment
// price, components, zone performance).
export default function DuctedSystemDetailPanel({
  selection,
  comparedSystemId,
  onComparedIdMapped,
  onComponentSwap
}: DuctedSystemDetailPanelProps) {
  const dispatch = useAppDispatch();
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const bundleMode = useAppSelector(selectDuctedBundleMode);
  const pairName = useAppSelector((state) =>
    selectDuctedPairName(state, selection?.systemUniqueId)
  );
  const existingSystemName = useAppSelector((state) =>
    selection
      ? selectDuctedPairExistingSystemLabel(state, selection.systemUniqueId)
      : null
  );
  const pairPick = useAppSelector((state) =>
    selection
      ? state.ui.ductedPairPicks[selection.systemUniqueId] ?? null
      : null
  );
  const pickedForPair = useAppSelector((state) =>
    selection
      ? isPickedForPair(
          state.ui.ductedPairPicks,
          selection.systemUniqueId,
          selection.cardId
        )
      : false
  );
  const instanceUniqueId = useAppSelector(
    (state) => state.project.instanceUniqueId
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );

  // Same live-Redux components source as the CCB modal: the selected card's
  // existing system, looked up fresh so Replacing/Keeping toggles elsewhere
  // are reflected in the Phase 5 positions.
  const components = useMemo(
    () =>
      selection
        ? existingSystems.find((s) => s.uniqueId === selection.systemUniqueId)
            ?.components ?? []
        : [],
    [existingSystems, selection]
  );

  // Same shared resolver on the same Redux inputs as the CCB-check
  // dispatchers (AHA-6796): forwarded into Phase 6 so the server can
  // compute tonnage-estimate ducted coverage. The loads are the selected
  // card's System Design pair when one is linked, else the project's.
  const totalBTUs = useAppSelector((state) =>
    selectDuctedPairLoads(state, selection?.systemUniqueId)
  );
  const coolingBtu = totalBTUs.cooling;
  const structure = useAppSelector((state) => state.structure.structure);
  const sizingBands = useAppSelector((state) => state.data.sizingBands);
  const tonnageSeed = useMemo(
    () =>
      resolveTonnageSeed({
        record: selection
          ? recommendationSelectionsBySystem[selection.systemUniqueId]
              ?.tonnage ?? null
          : null,
        components,
        coolingBtu
      }),
    [selection, recommendationSelectionsBySystem, components, coolingBtu]
  );

  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );
  const nameIndexes = useMemo(
    () => ductedDuplicateNameIndexes(systemsToCompare),
    [systemsToCompare]
  );
  const selectedSystems = useAppSelector(
    (state) => state.systems.selectedSystems
  );
  const { addSystemsToComparison, removeSystem } = useComparisonSelection();
  const [localComparedId, setLocalComparedId] = useState<string | undefined>();
  const [accessoryPicksByCard, setAccessoryPicksByCard] = useState<
    Record<string, AccessoryPicks>
  >({});

  useEffect(() => {
    setLocalComparedId(undefined);
  }, [selection?.cardId]);

  const rematchedId = comparedIdForSelection(
    new Map(
      comparedSystemId && selection
        ? [[selection.cardId, comparedSystemId]]
        : []
    ),
    systemsToCompare,
    selection
  );
  const comparedId = rematchedId ?? localComparedId;
  const alreadyChosen =
    comparedId != null &&
    systemsToCompare.some((system) => system.id === comparedId);

  const display = useMemo(() => {
    if (!selection) return null;
    return buildDuctedDetailDisplay({
      combination: selection.combination,
      productData: selection.productData,
      cardId: selection.cardId,
      systemUniqueId: selection.systemUniqueId,
      typeLabel: selection.typeLabel,
      keptPairing: selection.keptPairing,
      displayTypeLabel: selection.displayTypeLabel,
      components,
      existingSystem:
        existingSystems.find((s) => s.uniqueId === selection.systemUniqueId) ??
        null,
      structure,
      sizingBands,
      totalBTUs
    });
  }, [
    selection,
    components,
    existingSystems,
    structure,
    sizingBands,
    totalBTUs
  ]);

  // Shared CCB add-to-quotable pipeline (AHA-6681 CS-4). Unlike the CCB
  // modal's onSuccess (clear selection + close), the page consumer also puts
  // each added system into the comparison so ComparisonTabs renders it, and
  // maps the card so the rail Compare toggle stays in sync.
  const isAdding = usePendingCompatibilityAdd(
    selection ? Number(selection.combination.ID) : null
  );
  const { beginAdd } = useQuotableAdd({
    instanceUniqueId,
    components,
    existingSystemUniqueId: selection?.systemUniqueId,
    accessoryData: selection?.accessoryData ?? {},
    tonnageSeed,
    onSuccess: (added) => {
      addSystemsToComparison(
        added.map((system) =>
          selection ? withDuctedCompareIdentity(system, selection) : system
        )
      );
      const addedId = added[0]?.id;
      if (selection != null && addedId != null) {
        setLocalComparedId(addedId);
        onComparedIdMapped?.(selection.cardId, addedId, selection);
      }
    }
  });

  if (!selection) {
    return (
      <div className='flex h-full items-center justify-center text-gray-400'>
        Select a system on the left to view details.
      </div>
    );
  }

  const { combination, productData, accessoryData } = selection;
  const comparedSystemForName = systemsToCompare.find(
    (system) => system.id === comparedId
  );
  const displayTitle = numberedDuctedSystemLabel(
    selection.title,
    comparedSystemForName,
    nameIndexes
  );
  const displayTypeLabel = selection.typeLabel
    ? numberedDuctedSystemLabel(
        selection.typeLabel,
        comparedSystemForName,
        nameIndexes
      )
    : null;
  const showTypeLabel =
    displayTypeLabel !== null && displayTypeLabel !== displayTitle;

  // Same guard as the CCB row checkbox: the rail applies no such filter, so
  // an unresolvable slot (real model missing from the catalog enrichment)
  // must disable the add here. Missing instanceUniqueId stays a click-time
  // errorModal inside the pipeline, exactly as CCB behaves.
  const selectable = canSelectCombination(combination, productData);

  const rawAccessories = combination.Accessories as string | null | undefined;
  const localPicks = accessoryPicksByCard[selection.cardId] ?? {};
  const comparedSystem = alreadyChosen
    ? selectedSystems.find((system) => system.id === comparedId)
    : undefined;
  const accessoryPicks: AccessoryPicks = comparedSystem
    ? {
        ...localPicks,
        ...picksMatchingProductIds(
          groupCombinationAccessories(rawAccessories, accessoryData),
          quotableAccessoryProductIds(comparedSystem)
        )
      }
    : localPicks;
  const resolvedAccessories = resolveAccessoriesString(
    rawAccessories,
    accessoryData,
    accessoryPicks
  );

  const handleAccessoryPick = (
    accessoryTypeCode: string,
    pick: AccessoryPick
  ) => {
    setAccessoryPicksByCard((previous) => ({
      ...previous,
      [selection.cardId]: {
        ...previous[selection.cardId],
        [accessoryTypeCode]: pick
      }
    }));
  };

  const handleComponentSwap: DuctedComponentSwapHandler = (swapped) => {
    const followedPick =
      pickedForPair && pairPick ? syncDuctedPairPick(pairPick, swapped) : null;
    if (followedPick) {
      dispatch(
        setDuctedPairPick({
          systemUniqueId: selection.systemUniqueId,
          pick: followedPick
        })
      );
    }
    onComponentSwap?.(swapped);
  };

  const priceResult = computeCombinationTotalPrice(
    { ...combination, Accessories: resolvedAccessories },
    productData,
    accessoryData
  );

  let equipmentCostNode: React.ReactNode;
  if (priceResult.kind === 'total') {
    equipmentCostNode = formatPrice(priceResult.total);
  } else if (priceResult.kind === 'unavailable') {
    equipmentCostNode = (
      <span className='text-xs font-normal text-gray-400'>
        Price unavailable
      </span>
    );
  } else {
    equipmentCostNode = <span className='text-gray-400'>—</span>;
  }

  // Coverage and operating cost ride the same enrichment, so when the climate
  // lookup could not resolve both degrade the same way: dashes, with the
  // tooltip saying why.
  const operatingCost = readDuctedOperatingCost(combination);
  const annualTotalCost = operatingCost?.annualTotalCost ?? null;
  // Its own line only when the system leaves heat for space heaters to make
  // up; a system that carries every hour has nothing to say here, and the
  // total already includes the figure either way.
  const supplementalHeatingCost = supplementalHeatingCostLine(operatingCost);
  const ductedPerformance =
    ductedPerformanceFromCombination(combination) ??
    DUCTED_PERFORMANCE_UNAVAILABLE;

  // Ratings enrichment (AHA-6681): server-resolved display attributes ride
  // the combination row, including the fully resolved derived values
  // (kept-equipment > spec-estimate, resolved server-side so the card and
  // the range filters can never disagree); the applicability matrix hides
  // rows that can never apply to this combo's composition.
  const displayAttributes = parseDisplayAttributes(combination);
  const ratingVisible = ratingsApplicability(combination, displayAttributes);

  const derivedKeyByField = {
    CoolingCapacity: 'coolingCapacity',
    HeatingCapacity: 'heatingCapacity',
    AFUE: 'afue'
  } as const;

  const derivedPerfNode = (
    field: 'CoolingCapacity' | 'HeatingCapacity' | 'AFUE'
  ): React.ReactNode => {
    const raw = combination[field];
    if (raw != null) return formatPerfValue(raw);
    const derived = displayAttributes.derived[derivedKeyByField[field]];
    if (derived) {
      return (
        <DerivedValue
          formatted={formatPerfValue(derived.value)}
          suffix={
            derived.source === 'kept'
              ? keptSourcePhrase(derived.keptProductCode)
              : 'estimated'
          }
        />
      );
    }
    return '—';
  };

  const ratingRows: { key: RatingRowKey; node: React.ReactNode }[] = (
    [
      { key: 'SEER2', node: formatPerfValue(combination.SEER2) },
      { key: 'EER2', node: formatPerfValue(combination.EER2) },
      { key: 'HSPF2', node: formatPerfValue(combination.HSPF2) },
      { key: 'COP5', node: formatPerfValue(combination.COP5) },
      { key: 'AFUE', node: derivedPerfNode('AFUE') },
      { key: 'CoolingCapacity', node: derivedPerfNode('CoolingCapacity') },
      { key: 'HeatingCapacity', node: derivedPerfNode('HeatingCapacity') },
      {
        key: 'tonnage',
        node: formatTonnageLabel(displayAttributes.tonnage) ?? '—'
      },
      {
        key: 'refrigerant',
        node: displayAttributes.refrigerant.join(', ') || '—'
      },
      { key: 'stages', node: displayAttributes.stages.join(', ') || '—' },
      {
        key: 'fuelType',
        // LP display mapping (AHA-6796): an lp-token system type is
        // physically an NG furnace model + lp_conversion_kit, so its spec
        // fuelType can only ever say "Natural Gas" — display the honest
        // fuel instead. NG systems and code-less selections render the
        // spec value byte-identically.
        node:
          selection.typeCode && systemTypeCodeFuel(selection.typeCode) === 'lp'
            ? 'Propane (LP)'
            : displayAttributes.fuelType.join(', ') || '—'
      }
    ] as { key: RatingRowKey; node: React.ReactNode }[]
  ).filter((row) => ratingVisible[row.key]);

  const configLines = combinationConfigLines(combination, selection.modelSpecs);

  return (
    <div className='flex flex-col rounded-lg bg-white shadow-sm'>
      <div className='sticky top-0 z-10 bg-white'>
        <div className='flex flex-col gap-4 rounded-t-lg border border-gray-200 bg-white p-4'>
          <div className='flex items-center gap-4'>
            <div className='flex min-w-0 grow flex-col gap-1'>
              {bundleMode && pairName ? (
                <div className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
                  {pairName}
                  {existingSystemName ? ` · ${existingSystemName}` : ''}
                </div>
              ) : (
                existingSystemName && (
                  <div className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
                    Existing System: {existingSystemName}
                  </div>
                )
              )}
              <span className='text-xl font-bold text-gray-900'>
                {displayTitle}
              </span>
              {showTypeLabel && (
                <div className='text-sm text-gray-600'>{displayTypeLabel}</div>
              )}
              {selection.keptPairing && (
                <div>
                  <span className='inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-900'>
                    {KEPT_PAIRING_TAG}
                  </span>
                </div>
              )}
              <div className='flex flex-col text-sm text-gray-600'>
                {configLines.map((line) => (
                  <div key={line.label}>
                    {line.label}: {line.text}
                  </div>
                ))}
              </div>
            </div>
            <div className='shrink-0'>
              {bundleMode && pairName ? (
                <Button
                  color={pickedForPair ? 'green' : 'primary'}
                  text={compareActionText(
                    false,
                    pickedForPair,
                    pairSelectLabels(pairName)
                  )}
                  icon={pickedForPair ? CheckIcon : undefined}
                  iconSize='sm'
                  disabled={!selectable && !pickedForPair}
                  tooltip={
                    selectable || pickedForPair
                      ? undefined
                      : 'Product not available in catalog'
                  }
                  onClick={() => {
                    if (pickedForPair) {
                      dispatch(clearDuctedPairPick(selection.systemUniqueId));
                      return;
                    }
                    dispatch(
                      setDuctedPairPick({
                        systemUniqueId: selection.systemUniqueId,
                        pick: { selection, accessoryPicks }
                      })
                    );
                  }}
                />
              ) : (
                <Button
                  color={alreadyChosen ? 'green' : 'primary'}
                  text={compareActionText(
                    isAdding,
                    alreadyChosen,
                    PANEL_COMPARE_LABELS
                  )}
                  icon={alreadyChosen ? CheckIcon : undefined}
                  iconSize='sm'
                  loading={isAdding}
                  disabled={isAdding || (!selectable && !alreadyChosen)}
                  tooltip={
                    selectable || alreadyChosen
                      ? undefined
                      : 'Product not available in catalog'
                  }
                  onClick={() => {
                    if (isAdding) return;
                    if (alreadyChosen && comparedId != null) {
                      removeSystem(comparedId);
                      setLocalComparedId(undefined);
                      return;
                    }
                    beginAdd(
                      [
                        buildSelectedCombination(
                          combination,
                          productData,
                          selection.keptPairing,
                          selection.displayTypeLabel
                        )
                      ],
                      accessoryPicks
                    );
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-4 rounded-b-lg border-x border-b border-gray-200 p-4'>
        <div className='grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(230px,1fr))]'>
          <OverviewCard title='Ratings'>
            <table className='w-full text-xs'>
              <tbody>
                {ratingRows.map(({ key, node }) => (
                  <tr key={key}>
                    <td className='py-0.5 pr-3 font-medium text-gray-500'>
                      {RATING_LABELS[key]}
                    </td>
                    <td className='py-0.5 text-right text-gray-900'>{node}</td>
                  </tr>
                ))}
                {/* Rated-combo-only: the AHRI certificate is never derived. */}
                <tr>
                  <td className='py-0.5 pr-3 font-medium text-gray-500'>
                    AHRI Certificate #
                  </td>
                  <td className='py-0.5 text-right text-gray-900'>
                    {(combination.AHRICertificateNumber as string | null) ??
                      '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </OverviewCard>
          <OverviewCard title={<PerformanceInYourAreaTitle />}>
            <CoverageGrid sides={ductedPerformance} />
          </OverviewCard>
          <OverviewCard title='Cost'>
            <div className='divide-y divide-gray-100'>
              <CostRow label='Equipment' sublabel='(Excludes installation)'>
                {equipmentCostNode}
              </CostRow>
              <CostRow
                label='Cooling operating cost'
                sublabel={estimateSublabel(operatingCost?.coolingBasis ?? null)}
              >
                <OperatingCostAmount
                  annualCost={operatingCost?.annualCoolingCost ?? null}
                  unit='year'
                />
              </CostRow>
              <CostRow
                label='Heating operating cost'
                sublabel={estimateSublabel(operatingCost?.heatingBasis ?? null)}
              >
                <OperatingCostAmount
                  annualCost={operatingCost?.annualHeatingCost ?? null}
                  unit='year'
                />
              </CostRow>
              {supplementalHeatingCost !== null && (
                <CostRow
                  label='Supplemental heat'
                  sublabel='(Estimated — space heaters, at electric rates, for the heating hours this system cannot cover)'
                >
                  <OperatingCostAmount
                    annualCost={supplementalHeatingCost}
                    unit='year'
                  />
                </CostRow>
              )}
              <CostRow label='Total operating cost' sublabel='(Estimated)'>
                <OperatingCostAmount
                  annualCost={annualTotalCost}
                  unit='month'
                />
              </CostRow>
            </div>
          </OverviewCard>
        </div>

        <div>
          <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500'>
            System Components
          </div>
          <DuctedComponentsTable
            combination={combination}
            productData={productData}
            accessoryData={accessoryData}
            systemUniqueId={selection.systemUniqueId}
            accessoryPicks={accessoryPicks}
            onAccessoryPick={handleAccessoryPick}
            accessoryPicksDisabled={alreadyChosen}
            onComponentSwap={onComponentSwap && handleComponentSwap}
          />
        </div>

        {display && (
          <DuctedZonePerformanceSection
            system={display.system}
            existingEquipmentSize={existingSizeForDisplay(
              existingSystems.find(
                (system) => system.uniqueId === selection.systemUniqueId
              ) ?? null
            )}
          />
        )}
      </div>
    </div>
  );
}
